import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getDailySales } from '../../services/apiServices';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const TREND_DAYS = 14;
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

// Today's date in the admin's own timezone, as YYYY-MM-DD
const todayLocal = () => new Date().toLocaleDateString('en-CA', { timeZone });

const formatUsd = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  } catch (e) {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
};

const describeItem = (item) => {
  const plan = item.plan_type === 'topup' ? 'Top-up' : item.plan_type;
  const size = item.plan_type === 'unlimited' ? `${item.days} days` : `${item.gb} GB / ${item.days} days`;
  const count = item.esim_count > 1 ? ` × ${item.esim_count}` : '';
  return `${plan} · ${item.country} · ${size}${count}`;
};

function DailySales() {
  const [date, setDate] = useState(todayLocal());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getDailySales({ date, days: TREND_DAYS, tz: timeZone });
        if (!cancelled) {
          if (response?.status) setReport(response.data);
          else setError(response?.message || 'Could not load sales.');
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err.message || 'Could not load sales.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [date]);

  const summary = report?.summary;
  const trend = report?.trend || [];

  const chartData = {
    labels: trend.map((d) => new Date(`${d.date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })),
    datasets: [{
      label: 'Revenue (USD)',
      data: trend.map((d) => d.revenue_usd),
      backgroundColor: trend.map((d) => (d.date === report?.date ? '#ff5a1f' : '#ffc9b2')),
      borderRadius: 4,
    }],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `${formatUsd(ctx.raw)} · ${trend[ctx.dataIndex]?.sales || 0} sales`,
        },
      },
    },
    scales: { y: { beginAtZero: true, ticks: { callback: (v) => `$${v}` } } },
    onClick: (_, elements) => {
      if (elements.length) setDate(trend[elements[0].index].date);
    },
  };

  return (
    <div className="right-section main-page-cs daily-sales">
      <div className="customer-top">
        <div className="customer-top-left">
          <h2>Daily Sales</h2>
          <p className="daily-sales-note">Completed website orders and app top-ups. Times in {timeZone}.</p>
        </div>
        <div className="customer-top-right">
          <label htmlFor="daily-sales-date" className="visually-hidden">Date</label>
          <input
            id="daily-sales-date"
            type="date"
            className="form-control"
            value={date}
            max={todayLocal()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {loading && !report ? (
        <div className="loader-overlay"><span className="loader"></span></div>
      ) : summary && (
        <>
          <div className="row mt-3">
            {[
              ['Revenue (USD)', formatUsd(summary.revenue_usd)],
              ['Sales', summary.sales],
              ['New orders / Top-ups', `${summary.new_orders} / ${summary.topups}`],
              ['eSIMs sold', summary.esims],
            ].map(([label, value]) => (
              <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6" key={label}>
                <div className="dashboard-box">
                  <h4>{label}</h4>
                  <h3>{value}</h3>
                </div>
              </div>
            ))}
          </div>

          {Object.keys(summary.by_currency).length > 0 && (
            <p className="daily-sales-currencies">
              Charged:{' '}
              {Object.entries(summary.by_currency).map(([cur, amt]) => formatAmount(amt, cur)).join(' · ')}
              <span> (USD total uses the site's stored exchange rates)</span>
            </p>
          )}

          <div className="sales-graph daily-sales-chart">
            <h3>Last {TREND_DAYS} days</h3>
            <div style={{ height: 240 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>

          <div className="customer-tablle-main">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Type</th>
                  <th scope="col">Items</th>
                  <th scope="col">Amount</th>
                  <th scope="col">USD</th>
                </tr>
              </thead>
              <tbody>
                {report.sales.length === 0 ? (
                  <tr><td colSpan="7" className="text-center">No sales on this day.</td></tr>
                ) : report.sales.map((sale) => (
                  <tr key={sale.reference}>
                    <td>{sale.time}</td>
                    <td>{sale.reference}</td>
                    <td>{sale.customer_name || '—'}<br /><small>{sale.customer_email}</small></td>
                    <td>{sale.type === 'topup' ? 'Top-up' : 'New order'}</td>
                    <td>{sale.items.map((item, i) => <div key={i}>{describeItem(item)}</div>)}</td>
                    <td>{formatAmount(sale.amount, sale.currency)}</td>
                    <td>{sale.amount_usd !== null ? formatUsd(sale.amount_usd) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default DailySales;
